import React, { ComponentProps, useCallback, useMemo, useState } from "react";

import { useAppDispatch } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas";
import { FETCH_PARAM_MAPPINGS } from "cortex/ui/src/redux-store/ui-state/param-mappings/actions";

import { FetchController } from "../fetch-controller";

interface FetchControllerProps
  extends Omit<
    ComponentProps<typeof FetchController>,
    "fetchObject" | "objectId" | "object" | "objectLoaded"
  > {}

type ParamMappingsResponse = {
  data: { data: Record<string, Record<string, string>> };
};

function transform(
  payload: Record<string, Record<string, string>>,
): Set<string> {
  return Object.values(payload).reduce<Set<string>>((acc, v3map) => {
    Object.values(v3map).forEach((v3) => acc.add(v3));
    return acc;
  }, new Set<string>());
}

function FetchParamMappingController({ ...restProps }: FetchControllerProps) {
  const dispatch = useAppDispatch();
  const [legacyParams, setLegacyParams] = useState<Set<string>>(
    () => new Set(),
  );
  const [objectLoaded, setObjectLoaded] = useState(false);
  const objectId = useMemo(() => `param-maps-${Date.now()}`, []);

  const fetchObject = useCallback(() => {
    setObjectLoaded(false);

    return createPromiseApiAction(dispatch, FETCH_PARAM_MAPPINGS)
      .then((resp: unknown) => {
        const response = resp as ParamMappingsResponse;
        setLegacyParams(transform(response.data.data));
        setObjectLoaded(true);
        return response;
      })
      .catch((err: unknown) => {
        setObjectLoaded(true);
        throw err;
      });
  }, [dispatch]);

  return (
    <FetchController
      {...restProps}
      fetchObject={fetchObject}
      object={legacyParams}
      objectId={objectId}
      objectLoaded={objectLoaded}
    />
  );
}

export { FetchParamMappingController };
export default FetchParamMappingController;
